import { Skeleton } from 'antd';
import { useState } from 'react';

import { DataView, type DataViewColumn } from '@/components/DataView';
import { hueClasses, type Hue } from '@/components/hues';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate, formatDateTime } from '@core/format/dates';
import { formatAmount } from '@core/format/money';

import { useCompanies, useDues, type Company } from './api';
import { standingWords } from './billing';
import { dueList, filterCounts, filterLabels, type DueFilter } from './dues';
import { periodWords } from './prices';

/**
 * Every company on the platform, as a due list (docs/Platform_Billing_Design.md §6): what is owed
 * across them in three tiles, then the companies most urgent first, filterable to the two questions
 * the page is opened for. The billing column leads after the name because it is the one most likely
 * to need doing something about; "in setup" still says whether the panel may build the company
 * without its consent.
 */
export function CompaniesPage({
  canWrite,
  onNew,
  onOpen,
}: {
  canWrite: boolean;
  onNew: () => void;
  onOpen: (tenantId: number) => void;
}) {
  const companies = useCompanies();
  const [filter, setFilter] = useState<DueFilter>('all');

  const all = companies.data ?? [];
  const counts = filterCounts(all);
  const shown = dueList(all, filter);

  const columns: DataViewColumn<Company>[] = [
    {
      title: 'Company',
      key: 'name',
      primary: true,
      render: (_, c) => (
        <button
          type="button"
          className="cursor-pointer text-start"
          onClick={() => onOpen(c.tenantId)}
        >
          <span className="block font-semibold text-gray-900">{c.tenantName}</span>
          <span className="text-default block font-mono text-[length:var(--scale-field-hint)]">
            {c.tenantCode}
          </span>
        </button>
      ),
    },
    {
      title: 'Billing',
      key: 'billing',
      render: (_, c) => (
        <>
          <StatusBadge tone={standingWords[c.standing].tone}>
            {standingWords[c.standing].label}
          </StatusBadge>
          <span className="text-default block text-[length:var(--scale-field-hint)]">
            {c.paidUntil ? `Paid until ${formatDate(c.paidUntil)}` : 'No paid-until date'}
          </span>
        </>
      ),
    },
    {
      title: 'Terms',
      key: 'terms',
      render: (_, c) => (
        <>
          <span className="block">{c.planCode ?? '—'}</span>
          <span className="text-default block text-[length:var(--scale-field-hint)]">
            {c.agreedRecurringAmount !== null && c.billingPeriod
              ? `₹${formatAmount(c.agreedRecurringAmount)} ${periodWords[c.billingPeriod]}`
              : c.chargingModel === 'Pilot'
                ? 'Pilot'
                : 'No price'}
          </span>
        </>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, c) =>
        !c.isActive ? (
          <StatusBadge tone="negative">Inactive</StatusBadge>
        ) : c.wentLiveOn ? (
          <StatusBadge tone="positive">Live</StatusBadge>
        ) : (
          <StatusBadge tone="warning">In setup</StatusBadge>
        ),
    },
    { title: 'Branches', key: 'branches', render: (_, c) => c.activeBranches, hideOnCard: true },
    {
      title: 'Last sign-in',
      key: 'last',
      hideOnCard: true,
      render: (_, c) => (c.lastLoginOn ? formatDateTime(c.lastLoginOn) : 'Never'),
    },
    {
      title: '',
      key: 'open',
      trailing: true,
      render: (_, c) => (
        <Button icon="icon icon-arrow-right" onClick={() => onOpen(c.tenantId)}>
          Open
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Companies"
        subtitle="Most urgent first. A company in setup can be built from here without its consent; once live, only consented support access remains."
        actions={
          canWrite && (
            <Button variant="primary" icon="icon icon-plus" shortcut="create" onClick={onNew}>
              New company
            </Button>
          )
        }
      />

      <DuesTiles onFilter={setFilter} />

      <div
        role="group"
        aria-label="Show"
        className="border-border-color mb-3 inline-flex overflow-hidden rounded-md border bg-white"
      >
        {(Object.keys(filterLabels) as DueFilter[]).map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
            className={`cursor-pointer px-3 py-1.5 text-[length:var(--scale-field-hint)] whitespace-nowrap ${
              filter === key ? 'bg-dark font-medium text-white' : 'hover:bg-light text-gray-900'
            }`}
          >
            {filterLabels[key]} · {counts[key]}
          </button>
        ))}
      </div>

      <Card flush>
        <DataView
          columns={columns}
          dataSource={shown}
          loading={companies.isLoading}
          rowKey="tenantId"
          empty={
            filter === 'all' ? (
              <EmptyState
                title="No companies yet"
                hint="Create the first one to set up its branches, year, numbering and catalogue."
              />
            ) : (
              <EmptyState
                title={filter === 'overdue' ? 'Nobody is overdue' : 'Nothing falls due in 30 days'}
                hint="Show all to see every company."
              />
            )
          }
        />
      </Card>
    </>
  );
}

/**
 * The three tiles: `salesDashboard.tsx`'s stat card — label and figure left, the hue on a tile to
 * the right. One hue across the row, since they are one set; the overdue tile takes amber while it
 * holds anything, because a section holding a problem takes amber over its own hue and gives it
 * back when answered. Two of them open the filter they describe.
 */
function DuesTiles({ onFilter }: { onFilter: (filter: DueFilter) => void }) {
  const dues = useDues();
  const d = dues.data;
  const rupees = (n: number | undefined) => (n === undefined ? '—' : `₹${formatAmount(n)}`);

  return (
    <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Tile
        label="Due in the next 30 days"
        figure={rupees(d?.dueSoonAmount)}
        detail={d ? `${d.dueSoonCount} compan${d.dueSoonCount === 1 ? 'y' : 'ies'}` : ''}
        icon="icon icon-calendar-clock"
        hue="teal"
        loading={dues.isLoading}
        onOpen={() => onFilter('dueSoon')}
      />
      <Tile
        label="Overdue"
        figure={rupees(d?.overdueAmount)}
        detail={d ? `${d.overdueCount} compan${d.overdueCount === 1 ? 'y' : 'ies'}` : ''}
        icon="icon icon-clock-alert"
        hue={d && d.overdueCount > 0 ? 'warning' : 'teal'}
        loading={dues.isLoading}
        onOpen={() => onFilter('overdue')}
      />
      <Tile
        label="Received this month"
        figure={rupees(d?.receivedThisMonth)}
        detail={
          d && d.noTermsCount > 0
            ? `${d.noTermsCount} with no terms set`
            : 'Voided receipts left out'
        }
        icon="icon icon-badge-indian-rupee"
        hue="teal"
        loading={dues.isLoading}
      />
    </div>
  );
}

function Tile({
  label,
  figure,
  detail,
  icon,
  hue,
  loading,
  onOpen,
}: {
  label: string;
  figure: string;
  detail: string;
  icon: string;
  hue: Hue;
  loading: boolean;
  onOpen?: () => void;
}) {
  const body = (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-default mb-1 truncate text-[length:var(--scale-list-name)]">{label}</p>
        {loading ? (
          <Skeleton.Button active size="small" style={{ width: 64, minWidth: 64 }} />
        ) : (
          <h3 className="mb-0 truncate text-2xl font-bold text-gray-900 max-lg:text-xl">
            {figure}
          </h3>
        )}
        <p className="text-default mt-1 truncate text-[length:var(--scale-field-hint)]">{detail}</p>
      </div>
      <span
        className={`flex size-10 shrink-0 items-center justify-center rounded-md ${hueClasses[hue].tile}`}
        aria-hidden="true"
      >
        <i className={`${icon} text-lg text-white`} />
      </span>
    </div>
  );

  return (
    <Card flush>
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label={`${label}: ${figure}, ${detail}. Show them.`}
          className="block w-full cursor-pointer p-4 text-left"
        >
          {body}
        </button>
      ) : (
        <div className="p-4">{body}</div>
      )}
    </Card>
  );
}
